import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "qiyou-favorites";
const SYNC_EVENT = "qiyou-favorites-change";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // 本地存储不可用时静默失败，仅影响收藏状态
  }
  window.dispatchEvent(new Event(SYNC_EVENT));
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() =>
    typeof window === "undefined" ? [] : readFavorites(),
  );

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    const current = readFavorites();
    const next = current.includes(id)
      ? current.filter((item) => item !== id)
      : [id, ...current];
    writeFavorites(next);
    setFavorites(next);
    return next.includes(id);
  }, []);

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
    setFavorites([]);
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggleFavorite, clearFavorites, isFavorite };
}